/* import __COLOCATED_TEMPLATE__ from './sq-calendar-month.hbs'; */
/**
## Calendar Month
This component is used within [sq-input-calendar](/components/glass/sq-input-calendar.html).
It shows a grid of numbers with weekday headings, for the given month, and optionally
highlights a single day or range.

When a date is clicked, the `selectDate` action is triggered but this component doesn't update
any of its bindings - the component which uses sq-calendar-month must do that.

```example
@title Basic Example
@controller-properties
{
  beginDate: moment(new Date).subtract(1, 'days'),
  endDate: new Date()
}

@hbs
{{sq-calendar-month beginDate=beginDate
                    endDate=endDate
                    month=beginDate}}
```

### Parameters
@param {Date} beginDate The individual selected day, or start date in selected date range
@param {Date} endDate End date in selected date range
@param {Date} month The month shown in the calendar
@param {Object} disabledRange Object with beginDate and endDate values. This range of dates won't be able to be selected
@param {Boolean} [allowPastPageBack=true] Allows going back to previous months
@param {Boolean} [allowPastSelections=true] Allows selecting past date
@param {Boolean} [allowFuturePageForward=false] Allows going forward to next months
@param {Boolean} [allowFutureSelections=false] Allows selecting future date
 */
import { alias } from '@ember/object/computed';
import Component from '@ember/component';
import EmberObject, { computed } from '@ember/object';
import moment from 'moment';
import { ALL_TIME } from '@square/glass-ui/components/sq-input-calendar';
const DESIRED_WEEKS_INDICES = [0, 1, 2, 3, 4, 5];
const DESIRED_WEEKS = 6;
const DAYS_IN_A_WEEK_INDICES = [0, 1, 2, 3, 4, 5, 6];
const DAYS_IN_A_WEEK = 7;

// eslint-disable-next-line ember/no-classic-classes
const Day = EmberObject.extend({
  // Date instance
  value: null
});

// eslint-disable-next-line ember/no-classic-classes
const Week = EmberObject.extend({
  days: null,
  init(...args) {
    this._super(...args);
    return this.set('days', DAYS_IN_A_WEEK_INDICES.map(() => Day.create()));
  }
});

// eslint-disable-next-line ember/no-classic-classes
export default Component.extend({
  classNames: ['calendar--month'],
  month: null,
  beginDate: null,
  endDate: null,
  selectedDate: alias('beginDate'),
  weekdaysMin: computed(function () {
    return moment.weekdaysMin(true);
  }),
  // single cached property for the normalized highlighted range
  selectionRange: computed('beginDate', 'endDate', function () {
    const {
      beginDate,
      endDate
    } = this.getProperties('beginDate', 'endDate');

    // No selection range if no begin date or ALL_TIME
    if (!beginDate || beginDate === ALL_TIME && endDate === ALL_TIME) {
      return undefined;
    }
    const selectionBegin = moment(beginDate).clone().startOf('day');
    const selectionEnd = moment(endDate || beginDate).clone().startOf('day');
    return {
      selectionBegin,
      selectionEnd
    };
  }),
  // first and last day of given month:
  visibleRange: computed('month', function () {
    const month = this.get('month');
    if (!month) {
      return undefined;
    }
    const visibleBegin = moment(month).startOf('month');
    const visibleEnd = moment(month).endOf('month').startOf('day');
    return {
      visibleBegin,
      visibleEnd
    };
  }),
  // firstDay and lastDay of the weeks we want to show:
  paddedWeeks: computed('visibleRange', function () {
    const visibleRange = this.get('visibleRange');
    if (!visibleRange) {
      return undefined;
    }
    const {
      visibleBegin,
      visibleEnd
    } = visibleRange;
    const firstDay = visibleBegin.clone().startOf('week');
    const lastDay = visibleEnd.clone().endOf('week').startOf('day');
    while (Math.ceil(lastDay.diff(firstDay, 'days') / DAYS_IN_A_WEEK) < DESIRED_WEEKS) {
      const daysToFirst = visibleBegin.diff(firstDay, 'days');
      const daysFromLast = lastDay.diff(visibleEnd, 'days');
      if (daysToFirst <= daysFromLast) {
        firstDay.subtract(1, 'weeks');
      } else {
        lastDay.add(1, 'weeks');
      }
    }
    return {
      firstDay,
      lastDay
    };
  }),
  weeks: computed('_weeks', 'additionalRanges', 'allowFutureSelections', 'allowPastSelections', 'disabledRange', 'paddedWeeks', 'selectionRange', 'visibleRange', function () {
    // we'll cache these for more efficient views and stable action targets
    if (!this._weeks) {
      // eslint-disable-next-line ember/no-side-effects
      this.set('_weeks', DESIRED_WEEKS_INDICES.map(() => Week.create()));
    }
    const {
      selectionRange,
      visibleRange,
      paddedWeeks
    } = this.getProperties('selectionRange', 'visibleRange', 'paddedWeeks');
    if (!visibleRange || !paddedWeeks) {
      return this._weeks;
    }
    const {
      selectionBegin,
      selectionEnd
    } = selectionRange || {};
    const {
      visibleBegin,
      visibleEnd
    } = visibleRange;
    const {
      firstDay
    } = paddedWeeks;
    const today = moment().startOf('day');
    const currentDay = firstDay.clone();
    const disallowPastSelections = !this.get('allowPastSelections');
    const disallowFutureSelections = !this.get('allowFutureSelections');
    for (const week of this._weeks) {
      for (const day of week.get('days')) {
        this.beginPropertyChanges();
        day.setProperties({
          value: currentDay.clone().toDate(),
          date: currentDay.date(),
          // NOTE: (carden) calendar__day gets dasherized into calendar--day, yay :)
          calendar__dayIsToday: Number(currentDay) === Number(today),
          calendar__dayIsInCurrentMonth: visibleBegin <= currentDay && currentDay <= visibleEnd,
          calendar__dayIsDisallowed: disallowFutureSelections && Number(currentDay) > Number(today) || disallowPastSelections && Number(currentDay) < Number(today)
        });
        this._setRange(day, currentDay, selectionRange, selectionBegin, selectionEnd, 'calendar__dayIsSelected', 'calendar__dayIsSelectionBegin', 'calendar__dayIsSelectionEnd');
        const additionalRanges = this.get('additionalRanges');
        if (additionalRanges) {
          if (additionalRanges['primary']) {
            const range = additionalRanges['primary'];
            this._setRange(day, currentDay, range, range.beginDate, range.endDate, 'calendar__dayInPrimaryRange', 'calendar__dayIsPrimaryRangeBegin', 'calendar__dayIsPrimaryRangeEnd');
          }
          if (additionalRanges['secondary']) {
            const range = additionalRanges['secondary'];
            this._setRange(day, currentDay, range, range.beginDate, range.endDate, 'calendar__dayInSecondaryRange', 'calendar__dayIsSecondaryRangeBegin', 'calendar__dayIsSecondaryRangeEnd');
          }
        }
        const disabledRange = this.get('disabledRange');
        if (disabledRange) {
          this._setRange(day, currentDay, disabledRange, disabledRange.beginDate, disabledRange.endDate, 'calendar__dayInDisabledRange', 'calendar__dayIsDisabledRangeBegin', 'calendar__dayIsDisabledRangeEnd');
        }
        this.endPropertyChanges();
        currentDay.add(1, 'day');
      }
    }
    return this._weeks;
  }),
  _setRange(day, currentDay, range, begin, end, inRangeClass, isBeginClass, isEndClass) {
    day.set(inRangeClass, range && begin <= currentDay && currentDay <= end);
    day.set(isBeginClass, range && Number(currentDay) === Number(begin));
    day.set(isEndClass, range && Number(currentDay) === Number(end));
  },
  // bind to this to override default "yes past" config
  allowPastSelections: true,
  // bind to this to override default "no future" config
  allowFutureSelections: false,
  actions: {
    selectDate(date) {
      if (this.action) {
        this.action(date);
      }
      return false;
    }
  }
});